export const SUPPORTED_LOCALES = ['cn', 'en', 'es', 'fr', 'jp', 'kr', 'pt', 'ru', 'sa', 'tr'];

// Maps internal URL locale prefixes to valid BCP 47 language tags (per SCRUM-1323).
// Used for hreflang links and the HTML lang attribute.
export const LOCALE_BCP47_MAP: Record<string, string> = {
  cn: 'zh-Hans',
  en: 'en',
  es: 'es',
  fr: 'fr',
  jp: 'ja',
  kr: 'ko',
  pt: 'pt-BR',
  ru: 'ru',
  sa: 'ar',
  tr: 'tr',
};

// og:locale is NOT a BCP 47 tag. Open Graph wants `language_TERRITORY` (ISO 639-1 + ISO 3166-1),
// so deriving it from LOCALE_BCP47_MAP by swapping the dash for an underscore produces values
// Facebook does not recognise: `zh-Hans` became `zh_Hans` (Hans is a script subtag, not a
// territory) and bare tags like `en`, `es`, `ru` were emitted without any territory at all.
// Only pt-BR happened to come out right. Keep this map independent of the hreflang one.
// The territory must be a real country for that language: `ar_AR` read as Arabic-in-Argentina
// (SCRUM-2307). Arabic uses `ar_SA`, the same value Yoast emits for the WordPress blog.
export const LOCALE_OG_MAP: Record<string, string> = {
  cn: 'zh_CN',
  en: 'en_US',
  es: 'es_ES',
  fr: 'fr_FR',
  jp: 'ja_JP',
  kr: 'ko_KR',
  pt: 'pt_BR',
  ru: 'ru_RU',
  sa: 'ar_SA',
  tr: 'tr_TR',
};

export function localeToOgLocale(locale: string): string {
  return LOCALE_OG_MAP[locale] ?? 'en_US';
}

export function localeToHtmlLang(locale: string): string {
  return LOCALE_BCP47_MAP[locale] ?? 'en';
}

// Primary language subtags that are written right-to-left. Of the 10 supported
// locales only `sa` (BCP 47 `ar`) qualifies; the set exists so adding Hebrew,
// Persian or Urdu later is a one-line change instead of a new branch.
const RTL_LANGUAGE_SUBTAGS = new Set(['ar', 'he', 'fa', 'ur', 'ckb', 'azb']);
// Script subtags written right-to-left: a tag that names its script decides by it (pa-Arab-PK is RTL, pa-Guru is not;
// the home nova pairs have Punjabi in Shahmukhi, Sorani Kurdish and South Azerbaijani, all in Arabic script).
const RTL_SCRIPT_SUBTAGS = new Set(['arab', 'hebr']);
const LTR_SCRIPT_SUBTAGS = new Set(['latn', 'cyrl']);

/**
 * Resolves the `dir` attribute for the `<html>` element.
 *
 * Accepts either an internal locale prefix (`sa`) or an already-resolved BCP 47
 * tag (`ar`, `ar-SA`), because the two coexist as sources of truth for the
 * language of a document: most pages let `_document.tsx` derive the tag from the
 * routing locale, while `/blog/[slug]` and `/` pass one through
 * `pageProps.htmlLang`. `dir` is therefore resolved from whatever tag actually
 * reaches `<Html lang>`, so it can never contradict it — including a
 * region-qualified tag, which reading only the routing locale would have left
 * as `lang="ar-SA" dir="ltr"`.
 *
 * Anything unrecognised falls back to `ltr`, mirroring how `localeToHtmlLang`
 * falls back to the default locale instead of throwing.
 */
export function localeToDir(locale: string): 'rtl' | 'ltr' {
  const languageTag = LOCALE_BCP47_MAP[locale] ?? locale;
  const allSubtags = (typeof languageTag === 'string' ? languageTag : '').toLowerCase().split('-');
  // The script subtag only comes right after the language: everything from the first singleton on (an extension such as
  // ar-u-nu-latn, or private use such as pt-BR-x-ar) is not a script.
  const singleton = allSubtags.findIndex((subtag, index) => index > 0 && subtag.length === 1);
  const subtags = singleton >= 0 ? allSubtags.slice(0, singleton) : allSubtags;
  const primarySubtag = subtags[0] ?? '';
  const scriptSubtag = subtags.slice(1).find((subtag) => /^[a-z]{4}$/.test(subtag));
  if (scriptSubtag && RTL_SCRIPT_SUBTAGS.has(scriptSubtag)) return 'rtl';
  if (scriptSubtag && LTR_SCRIPT_SUBTAGS.has(scriptSubtag)) return 'ltr';
  return RTL_LANGUAGE_SUBTAGS.has(primarySubtag) ? 'rtl' : 'ltr';
}

const SITE_URL = 'https://noping.com';
const DEFAULT_LOCALE = 'en';

// A locale-prefixed root is `/pt`, never `/pt/` — production 308-redirects the trailing-slash form.
// Only the unprefixed English root keeps its slash, because `https://noping.com/` is the live 200.
// Without this, `'/pt' + '/'` produced `/pt/` and every canonical and hreflang on the 9 localized
// home pages pointed at a redirect, which Google may reinterpret (canonical) or drop from the
// cluster entirely (hreflang). Deeper paths were never affected: `'/pt' + '/aboutus'` is already fine.
function localeSuffix(pathname: string): string {
  return pathname === '/' ? '' : pathname;
}

export function generateHrefLangUrls(pathname: string, allowedLocales?: string[]): { lang: string; href: string }[] {
  const localesToUse = allowedLocales ? SUPPORTED_LOCALES.filter((locale) => allowedLocales.includes(locale)) : SUPPORTED_LOCALES;
  const hreflangs = localesToUse.map((locale) => ({
    lang: LOCALE_BCP47_MAP[locale] ?? locale,
    href: locale === 'en' ? `${SITE_URL}${pathname}` : `${SITE_URL}/${locale}${localeSuffix(pathname)}`,
  }));
  // x-default is the unprefixed English URL, so it is only emitted when English is part of the
  // cluster. A blog post written only in Portuguese has no `/blog/<slug>`: pointing x-default there
  // sent crawlers to a 404 on every non-English post (SCRUM-1957). Same x-default condition as blog-sitemap.ts.
  if (localesToUse.includes(DEFAULT_LOCALE)) hreflangs.push({ lang: 'x-default', href: `${SITE_URL}${pathname}` });
  return hreflangs;
}

// Query parameters allowed to remain in canonical URLs. Tracking and campaign
// parameters (utm_*, clickId, gclid, fbclid, etc.) are NOT in this list and
// will be stripped unconditionally.
const ALLOWED_CANONICAL_PARAMS = new Set<string>([]);

// Guards against callers interpolating values that resolved to undefined/null
// into the canonical path, which previously produced URLs like
// `/blog/undefined` or `/pro-settings/null` on pages that Cloudflare's
// challenge page intercepted before hydration (SCRUM-1459).
const BROKEN_SEGMENT_RE = /(^|\/)(undefined|null)(\/|$)/;

function isBrokenCanonicalPath(path: string): boolean {
  return !path || !path.startsWith('/') || BROKEN_SEGMENT_RE.test(path);
}

/**
 * Builds a safe canonical URL.
 *
 * - Undefined or null locale falls back to the default locale (en), which
 *   carries no locale prefix in the URL.
 * - Paths containing literal `/undefined` or `/null` segments are replaced
 *   with the site root so a broken interpolation never reaches Google.
 * - Any query parameters not in ALLOWED_CANONICAL_PARAMS (tracking params,
 *   utm_*, clickId, etc.) are stripped from the output.
 */
export function buildCanonical(locale: string | undefined | null, path: string): string {
  const safeLocale = locale && locale !== DEFAULT_LOCALE ? locale : '';
  const localePrefix = safeLocale ? `/${safeLocale}` : '';

  if (isBrokenCanonicalPath(path)) {
    if (typeof console !== 'undefined') console.error(`[buildCanonical] discarded broken path "${path}" — falling back to locale root.`);
    return `${SITE_URL}${localePrefix}`;
  }

  const [rawPath, rawQuery] = path.split('#')[0]!.split('?');
  // Next 308-redirects any trailing slash (`/pro-settings/` → `/pro-settings`, `/pt/` → `/pt`), and a
  // canonical that redirects is not a canonical. Only the unprefixed English root keeps its slash.
  const cleanPath = rawPath?.replace(/\/+$/, '') || '/';
  const basePath = `${SITE_URL}${localePrefix}${localePrefix ? localeSuffix(cleanPath) : cleanPath}`;

  if (!rawQuery) return basePath;

  const filtered = new URLSearchParams();
  new URLSearchParams(rawQuery).forEach((value, key) => {
    if (ALLOWED_CANONICAL_PARAMS.has(key)) filtered.set(key, value);
  });

  const filteredStr = filtered.toString();
  return filteredStr ? `${basePath}?${filteredStr}` : basePath;
}
